import { ENUM_PropertyInvestmentType } from '@api-new/common';
import { HTTP_CP_Company, HTTP_CP_LoadBranding_Response } from '@api-new/companyservice';
import { environment } from '@environments/environment';
import { createSelector } from '@ngrx/store';
import { AddSmartSearchPropertyMortgageSelectItem } from '@platform/smart-search/models/add-smart-search-property-mortgage-select-item.model';
import { PropertyMortgageSelectItem } from '@shared-lib/models/property-mortgage-select-item.model';
import { selectBranding } from '@shared/store/auth/auth.selectors';
import { selectCompany } from '@shared/store/company/company.selectors';
import { selectIsLoadingLenders, selectLenderEntities } from '@shared/store/lender/lenders.selectors';
import { selectIsLoadingMortgages, selectMortgages } from '@shared/store/mortgages/mortgages.selectors';
import { selectIsLoadingProperties, selectProperties } from '@shared/store/properties/properties.selectors';

export const selectPropertyMortgageSelectItems = createSelector(
  selectIsLoadingProperties,
  selectIsLoadingMortgages,
  selectIsLoadingLenders,
  selectProperties,
  selectMortgages,
  selectLenderEntities,
  (isLoadingProperties, isLoadingMortgages, isLoadingLenders, properties, mortgages, lenderEntities): PropertyMortgageSelectItem[] => {
    if (isLoadingProperties || isLoadingMortgages || isLoadingLenders || properties.length === 0) {
      return [];
    }
    const propertyMortgageSelectItems: PropertyMortgageSelectItem[] = properties.map((property) => ({
      propertyId: property.id,
      propertyName: property.name,
      propertyAddressLines: property.addressLines,
      propertyValue: property.roundedCurrentValue,
      isPropertyResidential: property.investmentType === ENUM_PropertyInvestmentType.PROPERTY_INVESTMENT_TYPE_RESIDENTIAL,
    }));

    mortgages.forEach((mortgage) => {
      const item = propertyMortgageSelectItems.find(
        (propertyMortgageSelectItem) => propertyMortgageSelectItem.propertyId === mortgage.propertyId,
      );
      item.mortgageId = mortgage.id;
      item.mortgageRoundedOutstandingBalance = mortgage.roundedCurrentOutstandingBalance;
      item.mortgageLenderLogo = lenderEntities[mortgage.lenderId].logo;
    });

    return propertyMortgageSelectItems;
  },
);

export const selectPropertyMortgageSelectItemsForAddSmartSearch = createSelector(
  selectIsLoadingProperties,
  selectIsLoadingMortgages,
  selectIsLoadingLenders,
  selectProperties,
  selectMortgages,
  selectLenderEntities,
  (
    isLoadingProperties,
    isLoadingMortgages,
    isLoadingLenders,
    properties,
    mortgages,
    lenderEntities,
  ): AddSmartSearchPropertyMortgageSelectItem[] => {
    if (isLoadingProperties || isLoadingMortgages || isLoadingLenders || properties.length === 0) {
      return [];
    }

    return mortgages.map((mortgage) => {
      const property = properties.find((property) => property.id === mortgage.propertyId);
      return {
        propertyId: property.id,
        propertyName: property.name,
        propertyAddressLines: property.addressLines,
        propertyValue: property.roundedCurrentValue,
        isPropertyResidential: property.investmentType === ENUM_PropertyInvestmentType.PROPERTY_INVESTMENT_TYPE_RESIDENTIAL,
        mortgageId: mortgage.id,
        mortgageLenderLogo: lenderEntities[mortgage.lenderId].logo,
        mortgageRoundedOutstandingBalance: mortgage.roundedCurrentOutstandingBalance,
        mortgageType: mortgage.mortgageType,
        mortgageCurrentMonthlyPayment: mortgage.currentMonthlyPayment,
        mortgageTermRemaining: mortgage.termRemaining,
      };
    });
  },
);

export const selectIsLoadingPlatformItems = createSelector(
  selectIsLoadingProperties,
  selectIsLoadingMortgages,
  selectIsLoadingLenders,
  (isLoadingProperties, isLoadingMortgages, isLoadingLenders): boolean => isLoadingLenders && isLoadingMortgages && isLoadingLenders,
);

export const selectIsExperianCompany = createSelector(
  selectCompany,
  selectBranding,
  (company: HTTP_CP_Company, branding: HTTP_CP_LoadBranding_Response) =>
    branding?.companyId === environment.partnerCompanies.experianCompanyId ||
    company?.id === environment.partnerCompanies.experianCompanyId,
);
